import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ModernLogo } from "@/components/ModernLogo";
import { SEO } from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useMembership } from "@/hooks/useMembership";
import { MEMBERSHIP, MEMBERSHIP_BENEFITS } from "@/config/membership";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle,
  MessageCircle,
  Percent,
  Search,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

const icons = [BookOpen, MessageCircle, Percent, Search, ShieldCheck];

export default function MembershipPage() {
  const { isMember, loading } = useMembership();
  const [starting, setStarting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoStarted = useRef(false);

  const startCheckout = async () => {
    setStarting(true);
    try {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) {
        navigate("/auth?redirect=/membership%3Fjoin%3D1");
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-membership-checkout");
      if (error) throw error;
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.href = data.url as string;
    } catch (error: any) {
      console.error("Membership checkout failed:", error);
      toast({
        title: "Could not start checkout",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (autoStarted.current) return;
    if (searchParams.get("join") !== "1") return;
    if (loading || isMember) return;
    autoStarted.current = true;
    startCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isMember, searchParams]);

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="Property Maintenance Membership £19.99/month | Sortable"
        description="Join the Sortable Trades & Property Maintenance Membership: monthly DIY guides, WhatsApp emergency advice, quote checking, trusted contractor directory and no booking fees."
        keywords="property maintenance membership, landlord maintenance plan, homeowner membership uk, quote checking service, trusted contractor directory, diy maintenance guides, discounted call outs"
        canonical="https://sortable.uk/membership"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Trades & Property Maintenance Membership",
          description:
            "Membership for UK homeowners and landlords: monthly DIY maintenance guides, emergency contractor advice by WhatsApp, discounted call-outs, quote checking and a trusted contractor directory.",
          brand: { "@type": "Organization", name: "Sortable" },
          offers: {
            "@type": "Offer",
            price: MEMBERSHIP.price,
            priceCurrency: "GBP",
            availability: "https://schema.org/InStock",
            url: "https://sortable.uk/membership",
          },
        }}
      />

      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ModernLogo showTagline={false} />
          </Link>
          <div className="flex items-center gap-1 md:gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-3 w-3 md:h-4 md:w-4 md:mr-2" />
                <span className="hidden md:inline">Back home</span>
              </Button>
            </Link>
            {isMember && (
              <Link to="/membership/hub">
                <Button size="sm">Member area</Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <section className="py-12 px-4">
        <div className="container mx-auto text-center max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 text-primary px-5 py-2 rounded-full text-sm font-semibold mb-6">
            <Sparkles className="h-4 w-4" />
            For homeowners and landlords
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-hero bg-clip-text text-transparent">
            Trades &amp; Property Maintenance Membership
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            Keep your property in order all year round, know when a quote is fair, and
            never pay a booking fee on a Sortable job again.
          </p>

          <Card className="max-w-md mx-auto border-primary/30 shadow-elegant">
            <CardHeader>
              <CardTitle className="text-4xl font-bold">
                {MEMBERSHIP.priceLabel}
                <span className="text-base font-normal text-muted-foreground">/month</span>
              </CardTitle>
              <CardDescription>Cancel anytime. No contract.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {isMember ? (
                <Button className="w-full" onClick={() => navigate("/membership/hub")}>
                  Go to your member area
                </Button>
              ) : (
                <Button className="w-full" onClick={startCheckout} disabled={starting || loading}>
                  {starting ? "Starting checkout…" : "Become a member"}
                </Button>
              )}
              <p className="text-xs text-muted-foreground">
                Secure payment by card. Your booking fee waiver applies immediately.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-12 px-4 bg-background/50">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-10">What's included</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {MEMBERSHIP_BENEFITS.map((benefit, index) => {
              const Icon = icons[index] ?? CheckCircle;
              return (
                <Card key={benefit.title} className="border-border/50">
                  <CardHeader>
                    <Icon className="h-6 w-6 text-primary mb-2" />
                    <CardTitle className="text-lg">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">
                      {benefit.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-12 px-4">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-8">Membership questions</h2>
          <div className="space-y-4">
            {[
              {
                q: "How does the discount on call-outs work?",
                a: "Members don't pay the 3% booking fee that normally sits on top of a tradesman's quote. On a £450 job that's £13.50 back in your pocket, every job.",
              },
              {
                q: "What does the quote checking service do?",
                a: "Send us the job details and the price you were quoted. We compare it against real UK job data and our tradesmen network, then tell you whether it's fair, high, or low.",
              },
              {
                q: "Can I cancel?",
                a: "Yes. Cancel any time from your member area and your benefits run to the end of the paid month.",
              },
            ].map((faq) => (
              <Card key={faq.q} className="border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">{faq.q}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 px-4 bg-gradient-hero text-primary-foreground text-center">
        <div className="container mx-auto">
          <Badge variant="secondary" className="mb-4">
            {MEMBERSHIP.priceLabel} per month
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Look after your property properly
          </h2>
          <Button
            size="lg"
            variant="secondary"
            onClick={isMember ? () => navigate("/membership/hub") : startCheckout}
            disabled={starting}
          >
            {isMember ? "Open member area" : "Join the membership"}
          </Button>
        </div>
      </section>
    </div>
  );
}
